import { useEffect, useMemo, useState } from 'react';
import Lightbox from './Lightbox';
import SectionHead from './SectionHead';

type RoomManifest = Record<string, string[]>;

function titleCase(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function RoomByRoom() {
  const [manifest, setManifest] = useState<RoomManifest | null>(null);
  const [activeRoom, setActiveRoom] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch('/rooms.json')
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load /rooms.json');
        return r.json();
      })
      .then((data) => {
        setManifest(data);
        const rooms = Object.keys(data);
        if (rooms.length) setActiveRoom(rooms[0]);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        setManifest({});
      });
  }, []);

  const rooms = useMemo(() => Object.keys(manifest || {}), [manifest]);

  const images = useMemo(() => {
    if (!manifest || !activeRoom) return [];
    return (manifest[activeRoom] || []).map((file) => ({
      src: `/images/rooms/${activeRoom}/${file}`,
      alt: `${titleCase(activeRoom)} - ${file.replace(/\.\w+$/, '')}`
    }));
  }, [manifest, activeRoom]);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () =>
    setLightboxIndex((i) =>
      i !== null ? (i - 1 + images.length) % images.length : null
    );
  const nextImage = () =>
    setLightboxIndex((i) => (i !== null ? (i + 1) % images.length : null));

  return (
    <section id="rooms" className="mx-auto max-w-6xl px-4 pb-14 scroll-mt-20">
      <SectionHead title="Room by room" />

      {/* Single scrollable row — no wrapping on mobile */}
      <div className="flex min-h-[42px] gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {rooms.map((room) => {
          const isActive = room === activeRoom;
          return (
            <button
              key={room}
              type="button"
              onClick={() => setActiveRoom(room)}
              aria-pressed={isActive}
              className={[
                'flex-none rounded-xl px-4 py-2 text-sm font-medium transition',
                'focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2',
                isActive
                  ? 'bg-forest text-white'
                  : 'border border-forest/20 bg-white text-bark hover:bg-glacial-light active:bg-glacial-light'
              ].join(' ')}
            >
              {titleCase(room)}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-forest">
          {activeRoom ? titleCase(activeRoom) : 'Photos'}
        </h3>
        <p className="text-sm text-bark/55">
          {images.length ? `${images.length} photos` : 'No photos yet'}
        </p>
      </div>

      <div className="mt-3 flex min-h-[274px] gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {images.map((img, index) => (
          <figure
            key={img.src}
            className="flex-none cursor-pointer overflow-hidden rounded-2xl border border-forest/10 bg-white shadow-xs transition hover:shadow-md"
          >
            <button
              type="button"
              onClick={() => openLightbox(index)}
              className="block focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2"
              aria-label={`View ${img.alt} full size`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="h-64 w-80 object-cover"
                loading="lazy"
              />
            </button>
          </figure>
        ))}
      </div>
      <p className="mt-1 text-xs text-bark/45 sm:hidden">
        Scroll to see more →
      </p>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
}
